import React, { useEffect, useMemo } from "react";

import {
  CheckBox,
  PrimaryButton,
  SecondaryButton,
  MultipleSelect,
  SearchField,
  ErrorMessage,
  SingleSelect,
} from "widgets";
import {
  useFetchGatewayTenants,
  useFetchPackages,
  GRADES,
  TARGET_EXAMS,
  useVerticalAndTenantsId,
} from "store/backend/assets";
import {
  useTenantsByVerticalId,
  filterTenantsByCountry,
  useTenantInfo,
} from "services/tenantService";
import Loader from "Shared/Loader/Loader";
import SelectedPackageSubscriptions from "components/OrderSummary/SelectedPackageSubscriptions";
import { KH_VERTICAL_ID } from "constants/config";
import { formatMonetaryAmount } from "utils/formatMonetaryAmount";
import { toast } from "react-fox-toast";
import FullPageLoader from "Shared/FullPageLoader/FullPageLoader";

import styles from "./PackageSubscription.module.scss";

const handlePackageSelection = (
  packageSubscription,
  selectedPackageSubscriptions,
  onPackagesSelected,
  filters
) => {
  const isAlreadySelected = selectedPackageSubscriptions.some(
    (p) => p.id === packageSubscription.id
  );

  if (isAlreadySelected) {
    const updatedSelections = selectedPackageSubscriptions.filter(
      (p) => p.id !== packageSubscription.id
    );
    onPackagesSelected(updatedSelections);
    return;
  }
  if (filters.selectedVerticalId === KH_VERTICAL_ID) {
    if (selectedPackageSubscriptions.length >= 1) {
      toast.warning("Only one package can be selected for this vertical!", {
        position: "top-center",
      });
      return;
    }
  }
  onPackagesSelected([...selectedPackageSubscriptions, packageSubscription]);
};

const handleOnClickRefreshButton = (onFilterChange) => {
  onFilterChange("selectedGrade", []);
  onFilterChange("selectedTargetExam", []);
};

const handleOnClickSearchButton = (refetch) => {
  refetch();
};

const isSearchButtonEnabled = (
  selectedTargetExam,
  selectedGrade,
  selectedVerticalId,
  selectedTenantId
) =>
  selectedTargetExam.length > 0 &&
  selectedGrade.length > 0 &&
  selectedVerticalId !== null &&
  selectedTenantId !== null;

const handleTenantChange = (event, onFilterChange, tenantMenuItems) => {
  const newTenantId = event.target.value;
  const selectedTenant = tenantMenuItems.find(
    (tenant) => tenant.value === newTenantId
  );
  if (selectedTenant) {
    onFilterChange("selectedTenantId", newTenantId);
    onFilterChange(
      "selectedGatewayTenantId",
      selectedTenant.gatewayTenantId ?? null
    );
    if (selectedTenant.state_id !== undefined) {
      onFilterChange("selectedOfficeLocationId", selectedTenant.state_id);
    }
  }
};

const buildCountryScopedCenterItems = ({
  countryScopedTenants,
  omsTenantsByGatewayTenantId,
}) =>
  countryScopedTenants
    .map((tenant) => {
      const omsTenantId = omsTenantsByGatewayTenantId.get(tenant.id);
      if (omsTenantId == null) {
        return null;
      }
      return {
        key: tenant.id,
        value: omsTenantId,
        label: tenant.name,
        gatewayTenantId: tenant.id,
      };
    })
    .filter(Boolean);

const filterPackagesByPreviousAcademicYear = (
  packages,
  isOrderTransfer,
  selectedVerticalId,
  previousOrderAcademicYear
) => {
  if (
    !isOrderTransfer ||
    Number(selectedVerticalId) !== Number(KH_VERTICAL_ID)
  ) {
    return packages;
  }

  return packages.filter(
    (pkg) =>
      pkg.academicYear != null &&
      String(pkg.academicYear).trim() !== "" &&
      Number(pkg.academicYear) === Number(previousOrderAcademicYear)
  );
};

const renderPackageDetails = (
  packageSubscription,
  selectedPackageSubscriptions,
  onPackagesSelected,
  filters,
  currencyCode,
) => {
  return (
    <div key={packageSubscription.id} className={styles.packageDetailBox}>
      <CheckBox
        className={styles.packageCheckBox}
        checked={selectedPackageSubscriptions.some(
          (p) => p.id === packageSubscription.id
        )}
        handleCheck={() =>
          handlePackageSelection(
            packageSubscription,
            selectedPackageSubscriptions,
            onPackagesSelected,
            filters
          )
        }
      />
      <div className={styles.packageDetailContent}>
        <div className={styles.label}>
          <span className={styles.detailTitle}>Package Name:</span>
          <span>{packageSubscription.displayName}</span>
        </div>
        <div className={styles.label}>
          <span className={styles.detailTitle}>Plan Name:</span>
          <span>{packageSubscription.planName}</span>
        </div>
        <div className={styles.label}>
          <span className={styles.detailTitle}>Validity:</span>
          <span>{packageSubscription.planValidity}</span>
        </div>
        <div className={styles.mrp}>
          <span className={styles.detailTitle}>MRP:</span>
          <span>
            {formatMonetaryAmount(packageSubscription.mrp, currencyCode)}
          </span>
        </div>
      </div>
    </div>
  );
};

const PackageSubscription = ({
  selectedPackageSubscriptions,
  onPackagesSelected,
  filters,
  searchTerm,
  isOrderTransfer,
  customerCountryId,
  previousOrderAcademicYear,
  onFilterChange,
  onSearchTermChange,
  handleChangePackageSubscriptionSelection,
  currencyCode,
}) => {
  const {
    selectedGrade,
    selectedTargetExam,
    selectedVerticalId,
    selectedTenantId,
  } = filters;

  const isCountryScopedCenters = Boolean(customerCountryId);
  const appliedFilters = {
    exams: selectedTargetExam,
    grade: selectedGrade,
    verticalId: selectedVerticalId,
    tenantId: selectedTenantId,
  };

  const {
    isFetching,
    isPending: isPackageDetailsLoading,
    data: packageDetails,
    error: packageDetailsError,
    refetch,
  } = useFetchPackages(appliedFilters, false);
  const {
    data: verticalAndTenantIds,
    isPending: isVerticalAndTenantIdsLoading,
  } = useVerticalAndTenantsId();

  const { data: verticalScopedTenants = [] } = useTenantsByVerticalId(
    isCountryScopedCenters ? selectedVerticalId : null
  );

  const countryScopedTenants = useMemo(
    () =>
      isCountryScopedCenters
        ? filterTenantsByCountry(verticalScopedTenants, customerCountryId)
        : [],
    [isCountryScopedCenters, verticalScopedTenants, customerCountryId]
  );

  const countryScopedGatewayTenantIds = useMemo(
    () => countryScopedTenants.map((tenant) => tenant.id),
    [countryScopedTenants]
  );

  const { data: countryScopedOmsTenants = [] } = useFetchGatewayTenants(
    isCountryScopedCenters ? selectedVerticalId : null,
    countryScopedGatewayTenantIds
  );

  const omsTenantsByGatewayTenantId = useMemo(
    () =>
      new Map(
        countryScopedOmsTenants.map((omsTenant) => [
          omsTenant.gatewayTenantId,
          omsTenant.id,
        ])
      ),
    [countryScopedOmsTenants]
  );

  const countryScopedCenterItems = useMemo(
    () =>
      isCountryScopedCenters
        ? buildCountryScopedCenterItems({
            countryScopedTenants,
            omsTenantsByGatewayTenantId,
          })
        : [],
    [isCountryScopedCenters, countryScopedTenants, omsTenantsByGatewayTenantId]
  );

  const selectedCenterGatewayTenantId =
    countryScopedCenterItems.find(
      (center) => String(center.value) === String(selectedTenantId)
    )?.gatewayTenantId ?? null;

  const { data: selectedCenterInfo } = useTenantInfo(
    isCountryScopedCenters ? selectedCenterGatewayTenantId : null
  );

  const selectedCenterStateId = selectedCenterInfo?.address?.state_id;

  useEffect(() => {
    if (!isCountryScopedCenters || selectedCenterGatewayTenantId == null) {
      return;
    }
    if (
      (filters.selectedGatewayTenantId ?? null) !== selectedCenterGatewayTenantId
    ) {
      onFilterChange("selectedGatewayTenantId", selectedCenterGatewayTenantId);
    }
  }, [
    isCountryScopedCenters,
    selectedCenterGatewayTenantId,
    filters.selectedGatewayTenantId,
    onFilterChange,
  ]);

  useEffect(() => {
    if (!isCountryScopedCenters || selectedCenterStateId == null) {
      return;
    }
    if ((filters.selectedOfficeLocationId ?? null) !== selectedCenterStateId) {
      onFilterChange("selectedOfficeLocationId", selectedCenterStateId);
    }
  }, [
    isCountryScopedCenters,
    selectedCenterStateId,
    filters.selectedOfficeLocationId,
    onFilterChange,
  ]);

  if (isVerticalAndTenantIdsLoading) {
    return <FullPageLoader />;
  }

  const packagesForDisplay = filterPackagesByPreviousAcademicYear(
    packageDetails || [],
    isOrderTransfer,
    selectedVerticalId,
    previousOrderAcademicYear
  );
  const filteredPackages = packagesForDisplay.filter((pkg) =>
    pkg.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const verticalMenuItems = verticalAndTenantIds?.map(({ id, name }) => ({
    key: id,
    value: id,
    label: name,
  }));

  const tenantMenuItems = isCountryScopedCenters
    ? countryScopedCenterItems
    : [];

  return (
    <div className={styles.mainContainer}>
      <div className={styles.packageContainer}>
        <div className={styles.verticalAndTenantIdContainer}>
          <SingleSelect
            name="vertical"
            value={selectedVerticalId}
            menuItems={verticalMenuItems}
            tag="Select a Vertical"
            disabled={true}
          />
          <SingleSelect
            name="tenant"
            value={selectedTenantId}
            handleChange={(event) =>
              handleTenantChange(event, onFilterChange, tenantMenuItems)
            }
            menuItems={tenantMenuItems}
            tag="Select a Center"
          />
        </div>
        {isCountryScopedCenters &&
          countryScopedTenants.length > 0 &&
          countryScopedCenterItems.length === 0 && (
            <ErrorMessage>
              No OMS centers mapped for this country. Please contact support.
            </ErrorMessage>
          )}
        <div className={styles.dropdownContainer}>
          <MultipleSelect
            multiple
            name="grade"
            tag="Select Grade"
            value={selectedGrade}
            menuItems={GRADES}
            handleChange={(event) =>
              onFilterChange("selectedGrade", event.target.value)
            }
            showCheckbox={true}
          />
          <MultipleSelect
            multiple
            name="targetExam"
            tag="Select Target Exam"
            value={selectedTargetExam}
            menuItems={TARGET_EXAMS}
            handleChange={(event) =>
              onFilterChange("selectedTargetExam", event.target.value)
            }
            showCheckbox={true}
          />
          <div className={styles.packageDetailsButtonContainer}>
            <PrimaryButton
              disabled={
                !isSearchButtonEnabled(
                  selectedTargetExam,
                  selectedGrade,
                  selectedVerticalId,
                  selectedTenantId
                )
              }
              onClick={() => handleOnClickSearchButton(refetch)}
            >
              Search
            </PrimaryButton>
            <SecondaryButton
              onClick={() => handleOnClickRefreshButton(onFilterChange)}
            >
              Clear
            </SecondaryButton>
          </div>
        </div>
        <div className={styles.searchFieldContainer}>
          <SearchField
            placeholder="Search by package name"
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
          />
        </div>
        <div className={styles.packageDetailsContainer}>
          {isPackageDetailsLoading && isFetching ? (
            <Loader />
          ) : (
            <>
              {packageDetailsError && (
                <ErrorMessage>{packageDetailsError.message}</ErrorMessage>
              )}
              {filteredPackages.length > 0
                ? filteredPackages.map((packageSubscription) =>
                    renderPackageDetails(
                      packageSubscription,
                      selectedPackageSubscriptions,
                      onPackagesSelected,
                      filters,
                      currencyCode,
                    )
                  )
                : !packageDetailsError && (
                    <p>Select filters to search Package Subscriptions.</p>
                  )}
            </>
          )}
        </div>
      </div>
      <div className={styles.selectedPackageSubscriptionsContainer}>
        <SelectedPackageSubscriptions
          selectedPackageSubscriptions={selectedPackageSubscriptions}
          handleChangePackageSubscriptionSelection={
            handleChangePackageSubscriptionSelection
          }
          currencyCode={currencyCode}
        />
      </div>
    </div>
  );
};

export default PackageSubscription;
